import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Alert, AlertDescription } from '../ui/Alert';
import { ResponsiveContainer } from '../ui/ResponsiveContainer';
import { ResponsiveGrid } from '../ui/ResponsiveGrid';
import { ResponsiveText } from '../ui/ResponsiveText';
import { PasswordInput } from '../ui/PasswordInput';
import { useUsersPaginated, useCreateUser, useUpdateUser, useDeleteUser, useBulkCreateUsers, useResendUserEmail } from '../../lib/hooks/useApi';
import { useHierarchyInfo } from '../../lib/hooks/useHierarchy';
import { User, UserCreate, UserUpdate, UserRole } from '../../lib/types/api';
import { Plus, Users, Edit, Trash2, Upload, Download, Mail, Shield, Users2, UserCheck, Key } from 'lucide-react';
import { apiClient } from '../../lib/api/client';
import { useConfirmation } from '../ui/ConfirmationDialog';
import { ManagerSelector } from '../ui/ManagerSelector';
import { SearchInput } from '../ui/SearchInput';

export const UserManagement: React.FC = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const { confirm } = useConfirmation();
  
  // Search and pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);
  
  // Ref for search input to maintain focus
  const searchInputRef = useRef<HTMLInputElement>(null);
  const wasFocusedRef = useRef<boolean>(false);
  
  const [formData, setFormData] = useState<Partial<UserCreate>>({
    email: '',
    first_name: '',
    last_name: '',
    role: 'recruiter',
    org_id: 'otomeyt-ai', // Default org ID
    manager_id: '',
    password: ''
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when search changes
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Memoize filters to prevent unnecessary re-renders
  const filters = useMemo(() => ({
    page: currentPage,
    page_size: pageSize,
    search: debouncedSearchTerm || undefined,
    role: roleFilter || undefined,
    is_active: activeFilter
  }), [currentPage, pageSize, debouncedSearchTerm, roleFilter, activeFilter]);

  const { data: usersData, isLoading: isLoadingUsers, error: usersError } = useUsersPaginated(filters);
  const users = usersData?.users || [];
  const pagination = usersData?.pagination;

  // Restore focus after data refetch if input was previously focused
  useEffect(() => {
    if (wasFocusedRef.current && searchInputRef.current && document.activeElement !== searchInputRef.current) {
      // Use setTimeout to ensure this runs after React has finished rendering
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          // Restore cursor position if possible
          const length = searchInputRef.current.value.length;
          searchInputRef.current.setSelectionRange(length, length);
        }
      }, 0);
    }
  }, [users]); // Restore focus when users data updates

  // Stable callback functions to prevent re-renders
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  // Track focus state
  const handleSearchFocus = useCallback(() => {
    wasFocusedRef.current = true;
  }, []);

  const handleSearchBlur = useCallback(() => {
    // Don't immediately clear - wait a bit in case focus moves temporarily
    setTimeout(() => {
      if (document.activeElement !== searchInputRef.current) {
        wasFocusedRef.current = false;
      }
    }, 100);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const handleRoleFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setRoleFilter(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleActiveFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setActiveFilter(value === '' ? undefined : value === 'true');
    setCurrentPage(1);
  }, []);

  // Hierarchy information
  const { data: hierarchyInfo } = useHierarchyInfo();
  
  // Debug logging for cache issues
  React.useEffect(() => {
    console.log('🔄 UserManagement: users data changed', {
      usersCount: users?.length || 0,
      totalCount: pagination?.total_count || 0,
      currentPage: pagination?.page || 1,
      isLoading: isLoadingUsers,
      error: usersError,
      timestamp: new Date().toISOString()
    });
  }, [users, pagination, isLoadingUsers, usersError]);
  const createUserMutation = useCreateUser({
    onSuccess: () => {
      setShowCreateForm(false);
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        role: 'recruiter',
        org_id: 'otomeyt-ai',
        manager_id: '',
        password: ''
      });
      showSuccessMessage('User created successfully!');
    },
    onError: (error) => {
      console.error('Failed to create user:', error);
    }
  });

  const updateUserMutation = useUpdateUser({
    onSuccess: () => {
      setEditingUser(null);
      showSuccessMessage('User updated successfully!');
    },
    onError: (error: Error) => {
      console.error('Failed to update user:', error);
    }
  });

  const deleteUserMutation = useDeleteUser({
    onSuccess: () => {
      showSuccessMessage('User deleted successfully!');
    },
    onError: (error: Error) => {
      console.error('Failed to delete user:', error);
    }
  });

  const resendEmailMutation = useResendUserEmail({
    onSuccess: (data) => {
      showSuccessMessage(`Onboarding email sent successfully to ${data.email}!`);
    },
    onError: (error: Error) => {
      console.error('Failed to resend email:', error);
    }
  });

  const bulkCreateUsersMutation = useBulkCreateUsers({
    onSuccess: (data) => {
      setShowBulkModal(false);
      setBulkFile(null);
      showSuccessMessage(`Created ${data.created_count} users, ${data.error_count} errors`);
    },
    onError: (error: Error) => {
      console.error('Failed to bulk create users:', error);
    }
  });

  const handleInputChange = (field: keyof UserCreate, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    const isPasswordRequired = !editingUser; // Only require password when creating new user
    if (!formData.email || !formData.first_name || !formData.last_name || !formData.role || !formData.org_id || (isPasswordRequired && !formData.password)) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate manager selection based on role
    if (formData.manager_id) {
      const selectedManager = users?.find((user: User) => user.id === formData.manager_id);
      if (!selectedManager) {
        confirm({
          title: 'Validation Error',
          description: 'Selected manager not found',
          variant: 'warning',
          confirmText: 'OK',
          onConfirm: () => {}
        });
        return;
      }
      
      if (!canBeManager(selectedManager.role)) {
        confirm({
          title: 'Validation Error',
          description: `Selected user (${selectedManager.role}) cannot be a manager. Only Team Lead and above can manage others.`,
          variant: 'warning',
          confirmText: 'OK',
          onConfirm: () => {}
        });
        return;
      }
    }

    // Validate role hierarchy
    if (formData.role === 'ceo' && formData.manager_id) {
      confirm({
        title: 'Validation Error',
        description: 'CEO should not have a manager',
        variant: 'warning',
        confirmText: 'OK',
        onConfirm: () => {}
      });
      return;
    }

    if (formData.role === 'admin' && formData.manager_id) {
      confirm({
        title: 'Validation Error',
        description: 'Admin should not have a manager',
        variant: 'warning',
        confirmText: 'OK',
        onConfirm: () => {}
      });
      return;
    }

    try {
      await createUserMutation.mutateAsync(formData as UserCreate);
    } catch (error) {
      console.error('Failed to create user:', error);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      org_id: user.org_id,
      manager_id: user.manager_id || '',
      password: '' // Don't populate password for editing
    });
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingUser) return;
    
    // Validate required fields
    if (!formData.email || !formData.first_name || !formData.last_name || !formData.role || !formData.org_id) {
      confirm({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'warning',
        confirmText: 'OK',
        onConfirm: () => {}
      });
      return;
    }

    // Validate manager selection based on role
    if (formData.manager_id) {
      const selectedManager = users?.find((user: User) => user.id === formData.manager_id);
      if (!selectedManager) {
        confirm({
          title: 'Validation Error',
          description: 'Selected manager not found',
          variant: 'warning',
          confirmText: 'OK',
          onConfirm: () => {}
        });
        return;
      }
      
      if (!canBeManager(selectedManager.role)) {
        confirm({
          title: 'Validation Error',
          description: `Selected user (${selectedManager.role}) cannot be a manager. Only Team Lead and above can manage others.`,
          variant: 'warning',
          confirmText: 'OK',
          onConfirm: () => {}
        });
        return;
      }
    }

    // Validate role hierarchy
    if (formData.role === 'ceo' && formData.manager_id) {
      confirm({
        title: 'Validation Error',
        description: 'CEO should not have a manager',
        variant: 'warning',
        confirmText: 'OK',
        onConfirm: () => {}
      });
      return;
    }

    if (formData.role === 'admin' && formData.manager_id) {
      confirm({
        title: 'Validation Error',
        description: 'Admin should not have a manager',
        variant: 'warning',
        confirmText: 'OK',
        onConfirm: () => {}
      });
      return;
    }

    try {
      const updateData: UserUpdate = {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: formData.role as UserRole,
        org_id: formData.org_id,
        manager_id: formData.manager_id || undefined,
        is_active: true
      };
      await updateUserMutation.mutateAsync({ id: editingUser.id, data: updateData });
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  const handleDelete = async (userId: string) => {
    confirm({
      title: 'Delete User',
      description: 'Are you sure you want to delete this user? This action cannot be undone.',
      variant: 'destructive',
      confirmText: 'Delete',
      onConfirm: async () => {
        await deleteUserMutation.mutateAsync(userId);
      }
    });
  };

  const handleResetPassword = async (userId: string, userName: string) => {
    confirm({
      title: 'Reset Password',
      description: `Are you sure you want to reset the password for ${userName}? A new password will be generated and sent to their email address.`,
      confirmText: 'Reset Password',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          const result = await apiClient.resetUserPassword(userId);
          showSuccessMessage(result.email_sent 
            ? `Password reset successfully! New password has been sent to ${userName}'s email.`
            : `Password reset successfully, but email could not be sent. Please contact ${userName} separately.`
          );
        } catch (error: any) {
          console.error('Failed to reset password:', error);
          showErrorMessage(error.response?.data?.detail || 'Failed to reset password');
        }
      }
    });
  };

  const handleResendEmail = async (userId: string, userEmail: string) => {
    confirm({
      title: 'Resend Onboarding Email',
      description: `Are you sure you want to resend the onboarding email to ${userEmail}?`,
      variant: 'info',
      confirmText: 'Send Email',
      onConfirm: async () => {
        await resendEmailMutation.mutateAsync(userId);
      }
    });
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      role: 'recruiter',
      org_id: 'otomeyt-ai',
      manager_id: '',
      password: ''
    });
  };


  // Helper function to determine if a role can be a manager
  const canBeManager = (role: string) => {
    return ['team_lead', 'manager', 'ops_head', 'ceo', 'admin'].includes(role);
  };

  // Helper function to get role hierarchy info
  const getRoleHierarchyInfo = (role: string) => {
    const hierarchy = {
      viewer: 'Can only view data, no management capabilities',
      recruiter: 'Reports to Team Lead or higher, manages candidates',
      team_lead: 'Manages recruiters, reports to Ops Head or CEO',
      manager: 'Manages teams and clients, reports to Ops Head or CEO',
      mis_coordinator: 'Coordinates MIS/analytics and admin functions',
      hr_coordinator: 'Coordinates HR operations and support tasks',
      ops_head: 'Manages Team Leads, reports to CEO',
      ceo: 'Top management, can manage all roles',
      admin: 'System administrator, full access'
    };
    return hierarchy[role as keyof typeof hierarchy] || 'Role information not available';
  };

  // Helper function to get manager name from manager_id
  const getManagerName = (managerId: string | undefined) => {
    if (!managerId) return 'No Manager';
    const manager = users.find(user => user.id === managerId);
    return manager ? `${manager.first_name} ${manager.last_name}` : 'Unknown Manager';
  };

  // Helper function to format last login date
  const formatLastLogin = (lastLogin: string | undefined) => {
    if (!lastLogin) return 'Never';
    try {
      const date = new Date(lastLogin);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return 'Invalid Date';
    }
  };

  const showSuccessMessage = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000); // Auto-hide after 3 seconds
  };

  const showErrorMessage = (message: string) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(''), 5000); // Auto-hide after 5 seconds
  };

  if (isLoadingUsers) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="xl" variant="ai" />
      </div>
    );
  }

  return (
    <ResponsiveContainer size="full" padding="md" maxWidth="6xl" className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Success Message */}
      {successMessage && (
        <Alert className="border-green-200 bg-green-50 text-green-800">
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}
      {errorMessage && (
        <Alert className="border-red-200 bg-red-50 text-red-800">
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

            {/* Hierarchy Information */}
            {hierarchyInfo && (
              <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-text-enterprise-primary">
                    <Shield className="h-5 w-5 text-primary-400" />
                    <ResponsiveText variant="heading-sm" weight="semibold">
                      Your Access Level
                    </ResponsiveText>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Current Role */}
                    <div className="flex items-center gap-3 p-3 bg-glass-surface/60 dark:bg-glass-surface-dark-mode/60 rounded-lg border border-glass-border dark:border-glass-border-dark-mode">
                      <UserCheck className="h-5 w-5 text-primary-400" />
                      <div>
                        <div className="text-sm font-medium text-text-enterprise-primary">Your Role</div>
                        <div className="text-sm text-primary-300 capitalize">{hierarchyInfo.user_role}</div>
                      </div>
                    </div>

                    {/* Access Scope */}
                    <div className="flex items-center gap-3 p-3 bg-glass-surface/60 dark:bg-glass-surface-dark-mode/60 rounded-lg border border-glass-border dark:border-glass-border-dark-mode">
                      <Users2 className="h-5 w-5 text-secondary-400" />
                      <div>
                        <div className="text-sm font-medium text-text-enterprise-primary">Access Scope</div>
                        <div className="text-sm text-secondary-300 capitalize">{hierarchyInfo.scope}</div>
                      </div>
                    </div>

                    {/* Can Manage Users */}
                    <div className="flex items-center gap-3 p-3 bg-glass-surface/60 dark:bg-glass-surface-dark-mode/60 rounded-lg border border-glass-border dark:border-glass-border-dark-mode">
                      <Users className="h-5 w-5 text-success-400" />
                      <div>
                        <div className="text-sm font-medium text-text-enterprise-primary">User Management</div>
                        <div className="text-sm text-success-300">
                          {hierarchyInfo.can_manage_users ? 'Enabled' : 'Disabled'}
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="flex items-center gap-3 p-3 bg-glass-surface/60 dark:bg-glass-surface-dark-mode/60 rounded-lg border border-glass-border dark:border-glass-border-dark-mode">
                      <Shield className="h-5 w-5 text-warning-400" />
                      <div>
                        <div className="text-sm font-medium text-text-enterprise-primary">Permissions</div>
                        <div className="text-sm text-warning-300">{hierarchyInfo.description}</div>
                      </div>
                    </div>
                  </div>

                  {/* Manageable Roles */}
                  {hierarchyInfo.manageable_roles.length > 0 && (
                    <div className="mt-4 p-3 bg-glass-surface/60 dark:bg-glass-surface-dark-mode/60 rounded-lg border border-glass-border dark:border-glass-border-dark-mode">
                      <div className="text-sm font-medium text-text-enterprise-primary mb-2">Roles You Can Manage:</div>
                      <div className="flex flex-wrap gap-2">
                        {hierarchyInfo.manageable_roles.map((role) => (
                          <span
                            key={role}
                            className="px-2 py-1 bg-primary-500/20 text-primary-300 text-xs rounded-full capitalize border border-primary-500/30"
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

      {/* Create/Edit User Form */}
      {(showCreateForm || editingUser) && (
        <Card className="glass-card overflow-visible relative z-20">
          <CardHeader>
            <CardTitle>
              <ResponsiveText variant="heading-md" weight="semibold">
                {editingUser ? 'Edit User' : 'Create New User'}
              </ResponsiveText>
            </CardTitle>
            <CardDescription>
              <ResponsiveText variant="body-sm" color="muted">
                {editingUser ? 'Update user information and permissions' : 'Add a new user to the system with appropriate role and permissions'}
              </ResponsiveText>
            </CardDescription>
            
            {/* Role Hierarchy Guide */}
            <div className="mt-4 p-3 bg-glass-surface/60 dark:bg-glass-surface-dark-mode/60 border border-glass-border dark:border-glass-border-dark-mode rounded-lg">
              <h4 className="text-sm font-medium text-text-enterprise-primary mb-2 flex items-center gap-2">
                <Shield className="h-4 w-4 text-primary-400" />
                Role Hierarchy Guide
              </h4>
              <div className="text-xs text-text-enterprise-secondary space-y-1">
                {hierarchyInfo?.scope === 'organization' && (
                  <>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-primary-300">CEO</span>
                      <span className="text-primary-400">→</span>
                      <span>Manages Ops Head, Admin</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-primary-300">Ops Head</span>
                      <span className="text-primary-400">→</span>
                      <span>Manages Team Leads</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-primary-300">Team Lead</span>
                      <span className="text-primary-400">→</span>
                      <span>Manages Recruiters</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-primary-300">Recruiter</span>
                      <span className="text-primary-400">→</span>
                      <span>Manages Candidates</span>
                    </div>
                  </>
                )}
                {hierarchyInfo?.scope === 'team' && (
                  <>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-primary-300">Team Lead/Manager</span>
                      <span className="text-primary-400">→</span>
                      <span>Manages Assigned Recruiters</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-primary-300">Recruiter</span>
                      <span className="text-primary-400">→</span>
                      <span>Manages Candidates</span>
                    </div>
                  </>
                )}
                {hierarchyInfo?.scope === 'self' && (
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-primary-300">Personal Access</span>
                    <span className="text-primary-400">→</span>
                    <span>Can only view and manage own profile</span>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="overflow-visible">
            <form onSubmit={editingUser ? handleUpdate : handleSubmit} className="space-y-4 overflow-visible">
              <ResponsiveGrid cols={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 3 }} gap="md" className="overflow-visible">
                <div>
                  <label className="text-xs sm:text-body-sm font-medium text-text-primary">Email</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="user@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs sm:text-body-sm font-medium text-text-primary">First Name</label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    placeholder="First Name"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs sm:text-body-sm font-medium text-text-primary">Last Name</label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    placeholder="Last Name"
                    required
                  />
                </div>
                {!editingUser && (
                  <PasswordInput
                    label="Password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="Password"
                    required
                  />
                )}

                <div>
                  <label className="text-xs sm:text-body-sm font-medium text-text-primary">
                    Role
                    <span className="text-xs text-text-muted ml-2">• Determines permissions and management level</span>
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    className="w-full px-2 sm:px-3 py-2 border border-glass-border dark:border-glass-border-dark-mode rounded-lg bg-glass-surface dark:bg-glass-surface-dark-mode text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 text-xs sm:text-sm"
                  >
                    {hierarchyInfo?.manageable_roles.map((role) => {
                      const roleDescriptions = {
                        'recruiter': 'Recruiter - Manages candidates, reports to Team Lead',
                        'team_lead': 'Team Lead - Manages recruiters, reports to Ops Head',
                        'manager': 'Manager - Manages teams and clients',
                        'mis_coordinator': 'MIS Coordinator - Analytics/ops coordination',
                        'hr_coordinator': 'HR Coordinator - HR operations support',
                        'ops_head': 'Ops Head - Manages Team Leads, reports to CEO',
                        'ceo': 'CEO - Top management, can manage all roles',
                        'admin': 'Admin - System administrator, full access',
                        'viewer': 'Viewer - Read-only access, no management'
                      };
                      return (
                        <option key={role} value={role}>
                          {roleDescriptions[role as keyof typeof roleDescriptions] || `${role.charAt(0).toUpperCase() + role.slice(1)}`}
                        </option>
                      );
                    })}
                  </select>
                  <p className="text-xs text-text-muted mt-1">
                    💡 {getRoleHierarchyInfo(formData.role || 'recruiter')}
                  </p>
                </div>
                <div>
                  <label className="text-xs sm:text-body-sm font-medium text-text-primary">Organization ID</label>
                  <Input
                    value={formData.org_id}
                    onChange={(e) => handleInputChange('org_id', e.target.value)}
                    placeholder="org-id"
                    required
                  />
                </div>
                <ManagerSelector
                  value={formData.manager_id || ''}
                  onChange={(managerId) => handleInputChange('manager_id', managerId)}
                  excludeUserId={editingUser?.id}
                />
              </ResponsiveGrid>
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                <Button
                  type="submit"
                  variant="gradient"
                  disabled={editingUser ? updateUserMutation.isPending : createUserMutation.isPending}
                  className="flex items-center justify-center space-x-2 w-full sm:w-auto"
                >
                  {editingUser ? (
                    updateUserMutation.isPending ? (
                      <LoadingSpinner size="sm" variant="ai" />
                    ) : (
                      <Edit className="w-4 h-4" />
                    )
                  ) : (
                    createUserMutation.isPending ? (
                      <LoadingSpinner size="sm" variant="ai" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )
                  )}
                  <span>{editingUser ? 'Update User' : 'Create User'}</span>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={editingUser ? handleCancelEdit : () => setShowCreateForm(false)}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      <Card className="glass-card relative z-10">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4 sm:w-5 sm:h-5" />
              <ResponsiveText variant="heading-md" weight="semibold">
                System Users
              </ResponsiveText>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => setShowCreateForm(true)}
                variant="gradient"
                size="sm"
                className="flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Add User</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBulkModal(true)}
                className="flex items-center space-x-2"
              >
                <Upload className="w-4 h-4" />
                <span>Bulk Upload</span>
              </Button>
            </div>
          </CardTitle>
          <CardDescription>
            <ResponsiveText variant="body-sm" color="muted">
              Manage existing user accounts and permissions
            </ResponsiveText>
          </CardDescription>
        </CardHeader>
        
        {/* Search and Filter Controls */}
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <SearchInput
                ref={searchInputRef}
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                placeholder="Search users by name or email..."
                className="w-full"
                onClear={handleClearSearch}
                showClearButton={!!searchTerm}
                autoComplete="off"
              />
              {debouncedSearchTerm && (
                <div className="absolute -bottom-6 left-0 text-xs text-text-muted">
                  Searching for: "{debouncedSearchTerm}"
                </div>
              )}
            </div>
            
            {/* Role Filter */}
            <div className="w-full sm:w-48">
              <select
                value={roleFilter}
                onChange={handleRoleFilterChange}
                className="w-full px-3 py-2 border border-glass-primary/20 dark:border-glass-primary/30 rounded-lg bg-glass-primary/10 dark:bg-glass-primary/20 backdrop-blur-sm text-text-enterprise-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
              >
                <option value="">All Roles</option>
                {hierarchyInfo?.manageable_roles.map((role) => (
                  <option key={role} value={role}>
                    {role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Active Status Filter */}
            <div className="w-full sm:w-48">
              <select
                value={activeFilter === undefined ? '' : activeFilter.toString()}
                onChange={handleActiveFilterChange}
                className="w-full px-3 py-2 border border-glass-primary/20 dark:border-glass-primary/30 rounded-lg bg-glass-primary/10 dark:bg-glass-primary/20 backdrop-blur-sm text-text-enterprise-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
          
          {/* Search Results Feedback */}
          {debouncedSearchTerm && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-blue-800 dark:text-blue-300">
                  🔍 Search results for "{debouncedSearchTerm}"
                </span>
                <button
                  onClick={handleClearSearch}
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
                >
                  Clear search
                </button>
              </div>
            </div>
          )}

          {/* Pagination Info */}
          {pagination && (
            <div className="flex items-center justify-between text-sm text-text-enterprise-secondary">
              <div>
                {debouncedSearchTerm ? (
                  <>
                    Found {pagination.total_count} user{pagination.total_count !== 1 ? 's' : ''} matching "{debouncedSearchTerm}"
                    {pagination.total_count > 0 && (
                      <span className="ml-2">
                        (Showing {((pagination.page - 1) * pagination.page_size) + 1} to {Math.min(pagination.page * pagination.page_size, pagination.total_count)})
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    Showing {((pagination.page - 1) * pagination.page_size) + 1} to {Math.min(pagination.page * pagination.page_size, pagination.total_count)} of {pagination.total_count} users
                  </>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <span>Page size:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-2 py-1 border border-glass-primary/20 dark:border-glass-primary/30 rounded bg-glass-primary/10 dark:bg-glass-primary/20 backdrop-blur-sm text-text-enterprise-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          )}
        </CardContent>
        <CardContent>
          {isLoadingUsers && (
            <div className="text-center py-6 sm:py-8">
              <LoadingSpinner size="lg" variant="ai" />
              <ResponsiveText variant="body-md" color="muted" className="mt-3 sm:mt-4">
                Loading users...
              </ResponsiveText>
            </div>
          )}
          
          {usersError && (
            <Alert variant="destructive">
              <AlertDescription>Failed to load users: {usersError.message}</AlertDescription>
            </Alert>
          )}
          
          {!isLoadingUsers && users && users.length > 0 ? (
            <div className="space-y-4">
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <div className="min-w-full">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-glass-primary/20 dark:border-glass-primary/30">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-text-enterprise-primary">User</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-text-enterprise-primary">Role</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-text-enterprise-primary">Reports to</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-text-enterprise-primary">Last Login</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-text-enterprise-primary">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-text-enterprise-primary">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user: User) => (
                        <tr key={user.id} className="border-b border-glass-primary/10 dark:border-glass-primary/20 hover:bg-glass-primary/5 dark:hover:bg-glass-primary/10 transition-colors">
                          <td className="py-4 px-4">
                            <div className="space-y-1">
                              <p className="font-medium text-text-enterprise-primary">{user.first_name} {user.last_name}</p>
                              <p className="text-xs text-text-enterprise-secondary">{user.email}</p>
                              <p className="text-xs text-text-enterprise-tertiary">ID: {user.id}</p>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full backdrop-blur-sm border ${
                              user.role === 'admin' ? 'bg-blue-500/20 dark:bg-blue-500/30 text-blue-700 dark:text-blue-300 border-blue-400/30 dark:border-blue-400/40' :
                              user.role === 'ops_head' ? 'bg-purple-500/20 dark:bg-purple-500/30 text-purple-700 dark:text-purple-300 border-purple-400/30 dark:border-purple-400/40' :
                              user.role === 'team_lead' ? 'bg-green-500/20 dark:bg-green-500/30 text-green-700 dark:text-green-300 border-green-400/30 dark:border-green-400/40' :
                              user.role === 'recruiter' ? 'bg-orange-500/20 dark:bg-orange-500/30 text-orange-700 dark:text-orange-300 border-orange-400/30 dark:border-orange-400/40' :
                              user.role === 'ceo' ? 'bg-red-500/20 dark:bg-red-500/30 text-red-700 dark:text-red-300 border-red-400/30 dark:border-red-400/40' :
                              user.role === 'manager' ? 'bg-yellow-500/20 dark:bg-yellow-500/30 text-yellow-700 dark:text-yellow-300 border-yellow-400/30 dark:border-yellow-400/40' :
                              user.role === 'mis_coordinator' ? 'bg-indigo-500/20 dark:bg-indigo-500/30 text-indigo-700 dark:text-indigo-300 border-indigo-400/30 dark:border-indigo-400/40' :
                              user.role === 'hr_coordinator' ? 'bg-pink-500/20 dark:bg-pink-500/30 text-pink-700 dark:text-pink-300 border-pink-400/30 dark:border-pink-400/40' :
                              'bg-background-300/20 dark:bg-background-dark-300/20 text-text-tertiary dark:text-text-tertiary border-border-200 dark:border-border-dark-200'
                            }`}>
                              {user.role.replace('_', ' ').toUpperCase()}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-text-enterprise-secondary text-sm">{getManagerName(user.manager_id)}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-text-enterprise-secondary text-sm">{formatLastLogin(user.last_login)}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full backdrop-blur-sm border ${
                              user.is_active 
                                ? 'bg-emerald-500/20 dark:bg-emerald-500/30 text-emerald-700 dark:text-emerald-300 border-emerald-400/30 dark:border-emerald-400/40' 
                                : 'bg-red-500/20 dark:bg-red-500/30 text-red-700 dark:text-red-300 border-red-400/30 dark:border-red-400/40'
                            }`}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex space-x-2">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="p-2 hover:bg-glass-primary/20 hover:border-primary-500/30 transition-colors"
                                onClick={() => handleEdit(user)}
                                title="Edit User"
                              >
                                <Edit className="w-4 h-4 text-text-enterprise-primary" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="p-2 hover:bg-glass-warning/20 hover:border-warning-500/30 text-warning-400 hover:text-warning-300 transition-colors"
                                onClick={() => handleResetPassword(user.id, `${user.first_name} ${user.last_name}`)}
                                title="Reset Password"
                              >
                                <Key className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="p-2 hover:bg-glass-secondary/20 hover:border-secondary-500/30 text-secondary-400 hover:text-secondary-300 transition-colors"
                                onClick={() => handleResendEmail(user.id, user.email)}
                                title="Resend Onboarding Email"
                                disabled={resendEmailMutation.isPending}
                              >
                                {resendEmailMutation.isPending ? (
                                  <LoadingSpinner size="sm" variant="ai" />
                                ) : (
                                  <Mail className="w-4 h-4" />
                                )}
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="p-2 hover:bg-glass-error/20 hover:border-error-500/30 text-error-400 hover:text-error-300 transition-colors"
                                onClick={() => handleDelete(user.id)}
                                title="Delete User"
                                disabled={deleteUserMutation.isPending}
                              >
                                {deleteUserMutation.isPending ? (
                                  <LoadingSpinner size="sm" variant="ai" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4">
                {users.map((user: User) => (
                  <div key={user.id} className="glass-card p-4 space-y-3 border border-glass-primary/20 dark:border-glass-primary/30">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-text-enterprise-primary truncate">
                          {user.first_name} {user.last_name}
                        </h3>
                        <p className="text-xs text-text-enterprise-secondary truncate">{user.email}</p>
                        <p className="text-xs text-text-enterprise-tertiary">ID: {user.id}</p>
                      </div>
                      <div className="flex space-x-1 ml-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="p-2 hover:bg-glass-primary/20 transition-colors"
                          onClick={() => handleEdit(user)}
                          title="Edit User"
                        >
                          <Edit className="w-4 h-4 text-text-enterprise-primary" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="p-2 hover:bg-glass-warning/20 text-warning-400 hover:text-warning-300 transition-colors"
                          onClick={() => handleResetPassword(user.id, `${user.first_name} ${user.last_name}`)}
                          title="Reset Password"
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="p-2 hover:bg-glass-error/20 text-error-400 hover:text-error-300 transition-colors"
                          onClick={() => handleDelete(user.id)}
                          title="Delete User"
                          disabled={deleteUserMutation.isPending}
                        >
                          {deleteUserMutation.isPending ? (
                            <LoadingSpinner size="sm" variant="ai" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full backdrop-blur-sm border ${
                        user.role === 'admin' ? 'bg-blue-500/20 text-blue-300 border-blue-400/30' :
                        user.role === 'ops_head' ? 'bg-purple-500/20 text-purple-300 border-purple-400/30' :
                        user.role === 'team_lead' ? 'bg-green-500/20 text-green-300 border-green-400/30' :
                        user.role === 'recruiter' ? 'bg-orange-500/20 text-orange-300 border-orange-400/30' :
                        user.role === 'ceo' ? 'bg-red-500/20 text-red-300 border-red-400/30' :
                        user.role === 'manager' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-400/30' :
                        user.role === 'mis_coordinator' ? 'bg-indigo-500/20 text-indigo-300 border-indigo-400/30' :
                        user.role === 'hr_coordinator' ? 'bg-pink-500/20 text-pink-300 border-pink-400/30' :
                        'bg-background-300/20 dark:bg-background-dark-300/20 text-text-tertiary dark:text-text-tertiary border-border-200 dark:border-border-dark-200'
                      }`}>
                        {user.role.replace('_', ' ').toUpperCase()}
                      </span>
                      <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full backdrop-blur-sm border ${
                        user.is_active 
                          ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30' 
                          : 'bg-red-500/20 text-red-300 border-red-400/30'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <div className="text-xs text-text-enterprise-secondary space-y-1">
                      <div><span className="font-medium">Organization:</span> {user.org_id}</div>
                      <div><span className="font-medium">Reports to:</span> {getManagerName(user.manager_id)}</div>
                      <div><span className="font-medium">Last Login:</span> {formatLastLogin(user.last_login)}</div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination Controls */}
              {pagination && pagination.total_pages > 1 && (
                <div className="flex items-center justify-center space-x-2 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2"
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={!pagination.has_previous}
                    className="px-3 py-2"
                  >
                    Previous
                  </Button>
                  
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                      const startPage = Math.max(1, currentPage - 2);
                      const pageNum = startPage + i;
                      if (pageNum > pagination.total_pages) return null;
                      
                      return (
                        <Button
                          key={pageNum}
                          variant={pageNum === currentPage ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className="px-3 py-2"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!pagination.has_next}
                    className="px-3 py-2"
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(pagination.total_pages)}
                    disabled={currentPage === pagination.total_pages}
                    className="px-3 py-2"
                  >
                    Last
                  </Button>
                </div>
              )}
            </div>
          ) : !isLoadingUsers && (
            <div className="text-center py-6 sm:py-8">
              <Users className="w-8 h-8 sm:w-12 sm:h-12 text-text-muted mx-auto mb-3 sm:mb-4" />
              <ResponsiveText variant="body-lg" color="secondary" className="mb-2">
                {debouncedSearchTerm ? `No users found matching "${debouncedSearchTerm}"` : 'No users found'}
              </ResponsiveText>
              <ResponsiveText variant="body-sm" color="muted" className="mb-4">
                {debouncedSearchTerm ? 'Try adjusting your search terms or filters' : 'Create your first user to get started'}
              </ResponsiveText>
              {debouncedSearchTerm && (
                <Button
                  onClick={handleClearSearch}
                  variant="outline"
                  size="sm"
                  className="mt-2"
                >
                  Clear Search
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Upload Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg bg-white dark:bg-surface-dark-100 rounded-xl shadow-xl border border-border-100 dark:border-border-dark-100">
            <div className="p-4 border-b border-border-100 dark:border-border-dark-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                <span className="font-semibold">Bulk Upload Users</span>
              </div>
              <button className="text-text-tertiary dark:text-text-tertiary hover:text-text-primary dark:hover:text-text-primary" onClick={() => setShowBulkModal(false)}>✕</button>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={async () => {
                  try {
                    const blob = await apiClient.downloadUsersSample();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'users_sample.csv';
                    a.click();
                    URL.revokeObjectURL(url);
                    showSuccessMessage('Sample CSV downloaded successfully!');
                  } catch (e) {
                    console.error('Failed to download sample CSV:', e);
                    confirm({
                      title: 'Download Failed',
                      description: 'Failed to download sample CSV. Please check your permissions and try again.',
                      variant: 'warning',
                      confirmText: 'OK',
                      onConfirm: () => {}
                    });
                  }
                }}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Sample CSV
                </Button>
              </div>
              <div>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-text-primary dark:text-text-primary file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-background-100 file:text-text-secondary hover:file:bg-background-200 dark:file:bg-surface-dark-100 dark:file:text-text-primary"
                />
                <p className="text-xs text-text-tertiary dark:text-text-tertiary mt-2">CSV columns: email, first_name, last_name, role, org_id, manager_email, phone_e164</p>
              </div>
            </div>
            <div className="p-4 border-t border-border-100 dark:border-border-dark-100 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setShowBulkModal(false)}>Cancel</Button>
              <Button disabled={!bulkFile || bulkCreateUsersMutation.isPending} onClick={() => {
                if (!bulkFile) return;
                bulkCreateUsersMutation.mutate({ file: bulkFile, sendEmails: true });
              }}>
                {bulkCreateUsersMutation.isPending ? 'Uploading...' : 'Upload CSV'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ResponsiveContainer>
  );
};

export default UserManagement;
